import React, { useMemo } from 'react';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { Link, Stack, Typography, LinearProgress, Chip } from '@mui/material';

import type { HealthCategoryId, IntakeStatusCustomSteps } from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from '../hooks';
import { CustomSteps } from './CustomSteps';

type GetCategoryProgressParams = {
  activeHealthCategoryId: HealthCategoryId | null;
  customSteps: IntakeStatusCustomSteps | null;
};

const getCategoryProgress = ({ activeHealthCategoryId, customSteps }: GetCategoryProgressParams) => {
  if (!activeHealthCategoryId) {
    return 0;
  }

  const category = customSteps?.[activeHealthCategoryId];

  if (!category) {
    return 0;
  }

  const categoryCustomSteps = Object.keys(category).filter((step) => !category[step]?.isDisabled);

  const completedSteps = categoryCustomSteps.filter(
    (step) => category[step]?.currentStatus?.status === 'COMPLETED',
  ).length;

  const totalSteps = categoryCustomSteps.length;

  return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 100;
};

type ProgressIndicatorProps = {
  activeHealthCategoryId: HealthCategoryId;
};

const ProgressIndicator = ({ activeHealthCategoryId }: ProgressIndicatorProps) => {
  const { customSteps } = usePatientIntakeStatusContext();

  const categoryProgress = useMemo(
    () => getCategoryProgress({ activeHealthCategoryId, customSteps }),
    [activeHealthCategoryId, customSteps],
  );

  if (!customSteps?.[activeHealthCategoryId]) {
    return null;
  }

  return (
    <Stack direction="column" alignItems="flex-start">
      <LinearProgress variant="determinate" value={categoryProgress} sx={{ width: 100 }} />
      <Typography variant="caption">{categoryProgress}% Complete</Typography>
    </Stack>
  );
};

type ActiveCategorySectionProps = {
  activeHealthCategoryId: HealthCategoryId | null;
};

export const ActiveCategorySection: React.FC<ActiveCategorySectionProps> = ({ activeHealthCategoryId }) => {
  const { isHealthcareProvider, customSteps, healthCategories } = usePatientIntakeStatusContext();

  if (!activeHealthCategoryId) {
    return null;
  }

  const numberOfCustomSteps = Object.keys(customSteps?.[activeHealthCategoryId] ?? {}).length;

  const status = healthCategories?.[activeHealthCategoryId];

  return (
    <Stack gap={1}>
      {isHealthcareProvider && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={0.5}>
            <Typography variant="caption">View Form</Typography>
            <Link
              href={`#form-section-${activeHealthCategoryId}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <LinkOutlinedIcon
                sx={{
                  fontSize: '0.75rem',
                  rotate: '-45deg',
                  '&:hover': { rotate: '-30deg' },
                  transition: 'rotate 0.2s',
                }}
              />
            </Link>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={1}>
            {status && status !== 'AWAITING_REVIEW' && (
              <Chip
                label={
                  status === 'CONSULT_COMPLETE'
                    ? 'Initial Intake Completion'
                    : status.replaceAll('_', ' ').toLowerCase()
                }
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            )}
            {numberOfCustomSteps >= 0 && <ProgressIndicator activeHealthCategoryId={activeHealthCategoryId} />}
          </Stack>
        </Stack>
      )}

      <CustomSteps activeHealthCategoryId={activeHealthCategoryId} />
    </Stack>
  );
};
